/**
 * User Menu Component
 *
 * Extracted from MainLayout for better code organization and memoization.
 * Displays user avatar, name, and dropdown with profile/admin/logout options.
 *
 * @see docs/EXEC-SPEC-PRODUCTION-READINESS.md Phase 4.4
 */

import { memo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { User, Settings, LogOut, ChevronDown } from 'lucide-react';
import type { User as UserType } from '../../../shared/types';
import { cn, getInitials } from '../../lib/utils';

interface UserMenuProps {
  user: UserType | null;
  onLogout: () => void;
}

/**
 * Desktop user menu with dropdown.
 * Memoized to prevent unnecessary re-renders when parent updates.
 */
function UserMenuComponent({ user, onLogout }: UserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);

  const handleToggle = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const handleClose = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleLogout = useCallback(() => {
    setIsOpen(false);
    onLogout();
  }, [onLogout]);

  return (
    <div className="relative hidden md:block">
      <button
        onClick={handleToggle}
        className={cn(
          'flex items-center gap-2 px-3 py-2 rounded-xl transition-all duration-200',
          'hover:bg-neutral-100 dark:hover:bg-neutral-800',
          isOpen && 'bg-neutral-100 dark:bg-neutral-800'
        )}
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <div className="avatar avatar-sm">
          {user?.avatarUrl ? (
            <img
              src={user.avatarUrl}
              alt={user.name}
              className="w-full h-full rounded-full object-cover"
            />
          ) : (
            getInitials(user?.name || 'U')
          )}
        </div>
        <span className="text-sm font-medium text-neutral-700 dark:text-neutral-300 max-w-[120px] truncate">
          {user?.name}
        </span>
        <ChevronDown
          className={cn(
            'w-4 h-4 text-neutral-500 transition-transform duration-200',
            isOpen && 'rotate-180'
          )}
        />
      </button>

      {isOpen && (
        <>
          {/* Backdrop to close menu on outside click */}
          <div className="fixed inset-0 z-10" onClick={handleClose} />

          {/* Dropdown menu */}
          <div className="dropdown-menu right-0 mt-2" role="menu">
            <Link
              to="/perfil"
              onClick={handleClose}
              className="dropdown-item"
              role="menuitem"
            >
              <User className="w-4 h-4" />
              Meu Perfil
            </Link>

            {user?.isAdmin && (
              <Link
                to="/admin"
                onClick={handleClose}
                className="dropdown-item"
                role="menuitem"
              >
                <Settings className="w-4 h-4" />
                Administração
              </Link>
            )}

            <div className="divider my-1" />

            <button
              onClick={handleLogout}
              className="dropdown-item-danger w-full"
              role="menuitem"
            >
              <LogOut className="w-4 h-4" />
              Sair
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Memoize to prevent re-renders when user object hasn't changed
export const UserMenu = memo(UserMenuComponent, (prevProps, nextProps) => {
  // Custom comparison - only re-render if user identity or admin status changed
  return (
    prevProps.user?.id === nextProps.user?.id &&
    prevProps.user?.name === nextProps.user?.name &&
    prevProps.user?.isAdmin === nextProps.user?.isAdmin &&
    prevProps.user?.avatarUrl === nextProps.user?.avatarUrl &&
    prevProps.onLogout === nextProps.onLogout
  );
});

UserMenu.displayName = 'UserMenu';

export default UserMenu;
